import { Driver } from '@citeproc-rs/wasm';
import { createContext, useState, useEffect, useRef, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from 'store';

import { createDriver, getStyleForDocument } from '../services/driver';
import { getUsedFields } from '../utils/fields';

interface RefDriverContextValue {
  usedFields: Record<string, Set<string>>;
  conf: {
    refStyle: string;
    refDisplay: string;
  };
  driver: React.RefObject<Driver | null>;
  setDriver: (config: { style: string }) => Promise<void>;
  batchedUpdates: any | null;
  updateBatch: () => void;
}

interface RefDriverProviderProps {
  children: ReactNode;
  doc: {
    ref_style: string;
    ref_display: string;
  };
}

export const RefDriverContext = createContext<RefDriverContextValue | null>(null);

export const RefDriverProvider: React.FC<RefDriverProviderProps> = ({ children, doc }) => {
  const driver = useRef<Driver | null>(null);
  const [batchedUpdates, setBatchedUpdates] = useState<any>(null);
  const [isReady, setIsReady] = useState(false);
  const [usedFields, setUsedFields] = useState<Record<string, Set<string>>>({});
  const [currentConf, setCurrentConf] = useState({
    refStyle: '',
    refDisplay: '',
  });

  const referenceToolbar = useAppSelector(state => state.toolbar.reference);
  const { i18n } = useTranslation();
  const lang = i18n.language === 'fr' ? 'fr-FR' : 'en-US';

  const changeDriver = async ({ style }: { style: string }): Promise<void> => {
    try {
      const fields = await getUsedFields(style);
      setUsedFields(fields);

      if (driver.current) {
        driver.current.free();
        driver.current = null;
      }

      driver.current = await createDriver({ lang, style });
    } catch (error) {
      console.error('Failed to change citation driver:', error);
      // You might want to add proper error handling UI here
    }
  };

  const updateBatch = (): void => {
    if (driver.current) {
      const updates = driver.current.batchedUpdates();
      setBatchedUpdates(updates);
    }
  };

  // Initialize driver on mount
  useEffect(() => {
    const initializeDriver = async () => {
      try {
        const style = getStyleForDocument(doc.ref_style, doc.ref_display);
        await changeDriver({ style });
        setCurrentConf({
          refStyle: doc.ref_style,
          refDisplay: doc.ref_display,
        });
        setIsReady(true);
      } catch (error) {
        console.error('Failed to initialize citation driver:', error);
        // You might want to add proper error handling UI here
      }
    };

    initializeDriver();

    // Cleanup on unmount
    return () => {
      if (driver.current) {
        driver.current.free();
        driver.current = null;
      }
    };
  }, []);

  // Handle style changes
  useEffect(() => {
    const handleStyleChange = async () => {
      if (
        referenceToolbar.refStyle &&
        referenceToolbar.refDisplay &&
        (referenceToolbar.refStyle !== currentConf.refStyle ||
          referenceToolbar.refDisplay !== currentConf.refDisplay)
      ) {
        try {
          const style = getStyleForDocument(
            referenceToolbar.refStyle,
            referenceToolbar.refDisplay
          );
          await changeDriver({ style });
          setCurrentConf({
            refStyle: referenceToolbar.refStyle,
            refDisplay: referenceToolbar.refDisplay,
          });
        } catch (error) {
          console.error('Failed to update citation style:', error);
          // You might want to add proper error handling UI here
        }
      }
    };

    handleStyleChange();
  }, [referenceToolbar]);

  if (!isReady) {
    return null; // Or a loading component
  }

  return (
    <RefDriverContext.Provider
      value={{
        usedFields,
        conf: currentConf,
        driver,
        setDriver: changeDriver,
        batchedUpdates,
        updateBatch,
      }}
    >
      {children}
    </RefDriverContext.Provider>
  );
};